import React, { useState, useEffect } from 'react';
import { X, Save, TestTube } from 'lucide-react';
import RuleBuilder from './RuleBuilder';

interface Condition {
  field: string;
  operator: string;
  value: any;
}

interface Rules {
  operator: 'AND' | 'OR';
  conditions: Condition[];
}

interface Segment {
  id?: number;
  name: string;
  description: string;
  inclusion_rules: Rules;
  exclusion_rules: Rules;
  priority: number;
  is_active: boolean;
  email_list_ready: boolean;
  never_contact: boolean;
  auto_tag: boolean;
}

interface TestResult {
  total_leads: number;
  matching_count: number;
  excluded_count: number;
  matching_leads: any[];
  excluded_leads: any[];
  match_percentage: string;
}

interface SegmentCreatorProps {
  segment?: Segment;
  onSave: (segment: Segment) => void;
  onCancel: () => void;
}

const SegmentCreator: React.FC<SegmentCreatorProps> = ({
  segment,
  onSave,
  onCancel
}) => {
  const [formData, setFormData] = useState<Segment>({
    name: '',
    description: '',
    inclusion_rules: { operator: 'AND', conditions: [] },
    exclusion_rules: { operator: 'OR', conditions: [] },
    priority: 5,
    is_active: true,
    email_list_ready: true,
    never_contact: false,
    auto_tag: true,
    ...segment
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [showTestResult, setShowTestResult] = useState(false);

  useEffect(() => {
    if (segment) {
      setFormData(segment);
    }
  }, [segment]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Segment name is required';
    }

    if ((!formData.inclusion_rules?.conditions || formData.inclusion_rules.conditions.length === 0) && !formData.never_contact) {
      newErrors.inclusion_rules = 'At least one inclusion rule is required (unless this is a "never contact" segment)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving segment:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleTestRules = async () => {
    if ((!formData.inclusion_rules?.conditions || formData.inclusion_rules.conditions.length === 0) && !formData.never_contact) {
      window.alert('Add at least one inclusion rule to test');
      return;
    }

    setTesting(true);
    try {
      const response = await fetch('/api/segments/evaluate-rules', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('chatbot_admin_token')}`
        },
        body: JSON.stringify({
          inclusion_rules: formData.inclusion_rules,
          exclusion_rules: formData.exclusion_rules
        })
      });

      if (response.ok) {
        const result = await response.json();
        setTestResult(result);
        setShowTestResult(true);
      } else {
        window.alert('Failed to test rules');
      }
    } catch (error) {
      console.error('Error testing rules:', error);
      window.alert('Network error testing rules');
    } finally {
      setTesting(false);
    }
  };

  const updateInclusionRules = (rules: Rules) => {
    setFormData(prev => ({ ...prev, inclusion_rules: rules }));
    setErrors(prev => ({ ...prev, inclusion_rules: '' }));
  };

  const updateExclusionRules = (rules: Rules) => {
    setFormData(prev => ({ ...prev, exclusion_rules: rules }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-6xl max-h-[90vh] overflow-y-auto w-full">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">
            {segment ? 'Edit Segment' : 'Create New Segment'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Segment Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-md ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="High-Value Autism Families"
              />
              {errors.name && (
                <p className="text-red-600 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority (1-100)
              </label>
              <input
                type="number"
                min="1"
                max="100"
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: Number(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
              <p className="text-xs text-gray-500 mt-1">Higher priority segments get leads first</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              rows={2}
              placeholder="Describe who this segment is for and how it will be used"
            />
          </div>

          {/* Segment Options */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="rounded"
              />
              <span className="text-sm">Active</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.auto_tag}
                onChange={(e) => setFormData(prev => ({ ...prev, auto_tag: e.target.checked }))}
                className="rounded"
              />
              <span className="text-sm">Auto-assign leads</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.email_list_ready}
                onChange={(e) => setFormData(prev => ({ ...prev, email_list_ready: e.target.checked }))}
                className="rounded"
              />
              <span className="text-sm">Email ready</span>
            </label>

            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.never_contact}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  never_contact: e.target.checked,
                  email_list_ready: e.target.checked ? false : prev.email_list_ready
                }))}
                className="rounded"
              />
              <span className="text-sm">Never contact</span>
            </label>
          </div>

          {/* Inclusion Rules */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-lg font-medium">Inclusion Rules</h3>
              <span className="text-sm text-gray-500">(Who SHOULD be in this segment)</span>
            </div>
            {errors.inclusion_rules && (
              <p className="text-red-600 text-sm mb-2">{errors.inclusion_rules}</p>
            )}
            <RuleBuilder
              rules={formData.inclusion_rules}
              onChange={updateInclusionRules}
              type="inclusion"
            />
          </div>

          {/* Exclusion Rules */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-lg font-medium">Exclusion Rules</h3>
              <span className="text-sm text-gray-500">(Who should NEVER be in this segment)</span>
            </div>
            <RuleBuilder
              rules={formData.exclusion_rules}
              onChange={updateExclusionRules}
              type="exclusion"
            />
          </div>

          {/* Test Rules */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium">Test Rules</h3>
              <button
                onClick={handleTestRules}
                disabled={testing || ((!formData.inclusion_rules?.conditions || formData.inclusion_rules.conditions.length === 0) && !formData.never_contact)}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-purple-700 disabled:opacity-50"
              >
                {testing ? (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                ) : (
                  <TestTube size={16} />
                )}
                Test Rules
              </button>
            </div>

            {showTestResult && testResult && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{testResult.total_leads}</div>
                    <div className="text-sm text-gray-600">Total Leads</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{testResult.matching_count}</div>
                    <div className="text-sm text-gray-600">Would Match</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{testResult.excluded_count}</div>
                    <div className="text-sm text-gray-600">Would Exclude</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{testResult.match_percentage}%</div>
                    <div className="text-sm text-gray-600">Match Rate</div>
                  </div>
                </div>

                {testResult.matching_leads.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-medium text-green-700 mb-2">Sample Matching Leads:</h4>
                    <div className="space-y-2">
                      {testResult.matching_leads.slice(0, 3).map((lead, index) => (
                        <div key={index} className="text-sm bg-green-50 p-2 rounded">
                          {lead.email} - {lead.parent_name} ({lead.child_name}) - Score: {lead.lead_score}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {testResult.excluded_leads.length > 0 && (
                  <div>
                    <h4 className="font-medium text-red-700 mb-2">Sample Excluded Leads:</h4>
                    <div className="space-y-2">
                      {testResult.excluded_leads.slice(0, 3).map((lead, index) => (
                        <div key={index} className="text-sm bg-red-50 p-2 rounded">
                          {lead.email} - {lead.parent_name} ({lead.child_name})
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          
          <button
            onClick={handleTestRules}
            disabled={testing || ((!formData.inclusion_rules?.conditions || formData.inclusion_rules.conditions.length === 0) && !formData.never_contact)}
            className="px-4 py-2 text-purple-700 border border-purple-300 rounded-lg hover:bg-purple-50 disabled:opacity-50"
          >
            <TestTube size={16} className="inline mr-1" />
            Test First
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? (
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
            ) : (
              <Save size={16} />
            )}
            {segment ? 'Update Segment' : 'Create Segment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SegmentCreator;